import { useCallback, useEffect, useState } from "react";
import {
  clearInstallPromptEvent,
  getInstallPromptEvent,
  initPwaInstallCapture,
  subscribeInstallPrompt,
  type BeforeInstallPromptEvent,
} from "./pwaInstallState";

type NavigatorWithStandalone = Navigator & { standalone?: boolean };

const isIOS = () =>
  typeof navigator !== "undefined" &&
  /iphone|ipad|ipod/i.test(navigator.userAgent);

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)")?.matches ||
  (navigator as NavigatorWithStandalone).standalone === true;

export type PWAStatus =
  | "ready"
  | "standalone"
  | "ios"
  | "waiting"
  | "prompted"
  | "installed"
  | "unknown";

export function usePwaInstall() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    () => getInstallPromptEvent(),
  );
  const [status, setStatus] = useState<PWAStatus>("unknown");
  const [reason, setReason] = useState<string>("");

  useEffect(() => {
    initPwaInstallCapture();

    if (isStandalone()) {
      setStatus("standalone");
      setReason("Already installed (standalone display-mode).");
      return;
    }
    if (isIOS()) {
      setStatus("ios");
      setReason("iOS Safari does not fire beforeinstallprompt.");
      return;
    }

    setStatus("waiting");
    setReason("Waiting for beforeinstallprompt to fire.");
  }, []);

  useEffect(() => {
    return subscribeInstallPrompt((event) => {
      setDeferred(event);
    });
  }, []);

  useEffect(() => {
    const onInstalled = () => {
      setDeferred(null);
      setStatus("installed");
      setReason("App installed (appinstalled event).");
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);

  useEffect(() => {
    if (status === "standalone" || status === "ios" || status === "installed") {
      return;
    }
    if (deferred) {
      setStatus("ready");
      setReason("Install prompt is available.");
      return;
    }
    if (status !== "prompted") {
      setStatus("waiting");
      setReason("Waiting for beforeinstallprompt to fire.");
    }
  }, [deferred, status]);

  const promptInstall = useCallback(async () => {
    if (!deferred) return { outcome: "unknown" as const };

    setStatus("prompted");
    setReason("Prompt shown; awaiting user choice.");

    await deferred.prompt();
    const choice = await deferred.userChoice;

    setDeferred(null);
    clearInstallPromptEvent();

    if (choice.outcome === "accepted") {
      setStatus("installed");
      setReason("User accepted install prompt.");
    } else {
      setStatus("waiting");
      setReason("User dismissed install prompt. Chrome throttles re-prompts.");
    }

    return choice;
  }, [deferred]);

  const isInstallable = status === "ready";

  return {
    isInstallable,
    status,
    reason,
    promptInstall,
    isIOS: isIOS(),
    isStandalone: isStandalone(),
  };
}
